import { azureStorageChipsToOData, azureStorageODataAstToChips, canonicalAzureStorageOData, formatAzureStorageOData, validateAzureStorageOData, type AzureStoragePropertyTypes } from '../../../../../core/engines/azureStorageOData';
import type { FilterCondition, FilterGroup, StorageExplorerEntityView } from '../../../core/types/workspaceTypes';
import { decodePersistedCondition } from './entityViewPersistence';

export const ENTITY_PAGE_SIZES = [100, 250, 500, 1000] as const;
export const DEFAULT_ENTITY_VIEW: StorageExplorerEntityView = {
  filters: [], filterMode: 'wizard', selectedColumns: '*', pageSize: 100,
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const ALLOWED_OPERATORS: ReadonlySet<FilterCondition['operator']> = new Set(['=', '!=', '<', '<=', '>', '>=', 'LIKE', 'BETWEEN', 'enum']);

/** Decodes the session shape (`entityViewPersistence.ts`): typed `{ t, v }` operands keep their EDM tags; an illegal value drops its condition. */
const coerceConditions = (value: unknown): FilterCondition[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((candidate) => {
    if (!isRecord(candidate) || typeof candidate.operator !== 'string' || !ALLOWED_OPERATORS.has(candidate.operator as FilterCondition['operator'])) return [];
    const condition = decodePersistedCondition(candidate);
    return condition ? [condition] : [];
  });
};

export const coerceEntityFilterGroups = (value: unknown): FilterGroup[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((candidate) => {
    if (!isRecord(candidate) || typeof candidate.field !== 'string' || candidate.field.trim() === '') return [];
    const conditions = coerceConditions(candidate.conditions);
    return conditions.length > 0 ? [{ field: candidate.field, conditions }] : [];
  });
};

export function coerceEntityViewState(value: unknown): StorageExplorerEntityView {
  if (!isRecord(value)) return { ...DEFAULT_ENTITY_VIEW };
  const requestedSize = typeof value.pageSize === 'number' && Number.isFinite(value.pageSize) ? value.pageSize : DEFAULT_ENTITY_VIEW.pageSize;
  const pageSize = ENTITY_PAGE_SIZES.reduce((best, candidate) => requestedSize >= candidate ? candidate : best, ENTITY_PAGE_SIZES[0]);
  const selectedColumns = value.selectedColumns === '*'
    ? '*'
    : Array.isArray(value.selectedColumns)
      ? [...new Set(value.selectedColumns.filter((item): item is string => typeof item === 'string' && item.length > 0))]
      : '*';
  return {
    filters: coerceEntityFilterGroups(value.filters),
    ...(typeof value.odataFilter === 'string' && value.odataFilter.trim() ? { odataFilter: value.odataFilter } : {}),
    filterMode: value.filterMode === 'odata' ? 'odata' : 'wizard',
    selectedColumns,
    pageSize,
  };
}

export function canonicalEntityFilter(view: StorageExplorerEntityView, propertyTypes: AzureStoragePropertyTypes): string | undefined {
  if (view.filterMode === 'odata' && view.odataFilter?.trim()) {
    // Observed entity types are not a table schema: a raw literal is validated
    // for its own syntax only (S4 review C, M4) — `Metric gt 1.5` is legal even
    // when the first observed Metric was Int32.
    const validation = validateAzureStorageOData(view.odataFilter);
    if (!validation.ok) throw new Error(validation.error.message);
    return formatAzureStorageOData(validation.ast);
  }
  if (view.filterMode === 'odata') return undefined;
  return view.filters.length > 0 ? azureStorageChipsToOData(view.filters, propertyTypes) : undefined;
}

export type EntityFilterModeSwitch = { ok: true; view: StorageExplorerEntityView } | { ok: false; reason: string };
export const UNREPRESENTABLE_AS_CHIPS = 'This raw filter cannot be shown as filter chips. Edit it as OData or clear it first.';

const sameFilter = (raw: string, canonical: string): boolean => {
  try { return canonicalAzureStorageOData(raw) === canonical; } catch { return false; }
};

/**
 * Switch between chips and raw OData WITHOUT changing the active filter (S4
 * review B, R8): raw → chips converts the applied raw filter through the same
 * representability guard as "Convert to chips" (refused when lossy; no raw
 * filter → no chips, never stale ones); chips → raw writes the active chips as
 * OData, keeping the stored raw text only while it still means the same thing.
 */
export function switchEntityFilterMode(
  view: StorageExplorerEntityView,
  filterMode: StorageExplorerEntityView['filterMode'],
  propertyTypes: AzureStoragePropertyTypes,
): EntityFilterModeSwitch {
  if (view.filterMode === filterMode) return { ok: true, view };
  if (filterMode === 'wizard') {
    const raw = view.odataFilter?.trim();
    if (!raw) return { ok: true, view: { ...view, filterMode, filters: [] } };
    const parsed = validateAzureStorageOData(raw);
    const chips = parsed.ok ? azureStorageODataAstToChips(parsed.ast, propertyTypes) : undefined;
    return chips ? { ok: true, view: { ...view, filterMode, filters: chips } } : { ok: false, reason: UNREPRESENTABLE_AS_CHIPS };
  }
  const { odataFilter, ...rest } = view;
  if (view.filters.length === 0) return { ok: true, view: { ...rest, filterMode } };
  try {
    const canonical = azureStorageChipsToOData(view.filters, propertyTypes);
    return { ok: true, view: { ...rest, filterMode, odataFilter: odataFilter && sameFilter(odataFilter, canonical) ? odataFilter : canonical } };
  } catch (error) {
    return { ok: false, reason: error instanceof Error ? error.message : 'These filter chips cannot be written as OData.' };
  }
}

export type LoadedEntitySort = { column: string; direction: 'asc' | 'desc' };
export function sortLoadedEntityRows<T extends Record<string, unknown>>(rows: readonly T[], sort: LoadedEntitySort | undefined): T[] {
  if (!sort) return [...rows];
  const direction = sort.direction === 'asc' ? 1 : -1;
  return rows.map((row, index) => ({ row, index })).sort((left, right) => {
    const a = left.row[sort.column];
    const b = right.row[sort.column];
    if (a === b) return left.index - right.index;
    if (a === undefined || a === null) return 1;
    if (b === undefined || b === null) return -1;
    return (a < b ? -1 : 1) * direction;
  }).map(({ row }) => row);
}
